import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import usePageTitle from 'hooks/usePageTitle';
import { Joyride } from 'react-joyride';
import { getJoyrideScrollOffset } from 'utils/joyrideConfig';
import PageMenu from "../../blocks/PageMenu/PageMenu";
import Icon from "../../blocks/Icon/Icon";
import Loader from 'components/blocks/Loader/Loader';
import { SignalRContext } from "../../../contexts/SignalRContext";
import useUpdateNotification from "../../../services/notifications/useUpdateNotification";
import { useCheckTypeNotification } from "hooks/useCheckTypeNotification";
import useGetAllNotificationsByUser from "../../../services/notifications/useGetAllNotificationsByUser";
import useDecode from "hooks/useDecode";
import useGetRole from "hooks/useGetRole";
import InputDatePicker from "../../blocks/Inputs/InputDatePicker";
import { useNotificationsTour } from "hooks/useNotificationsTour";
import "../../blocks/CommsList/CommsList.scss";
import "./notifications.scss";

const JOYRIDE_PROPS = {
  continuous: true,
  scrollToFirstStep: true,
  locale: { back: 'Anterior', close: 'Cerrar', last: 'Finalizar', next: 'Siguiente', nextWithProgress: 'Siguiente ({current} de {total})', open: 'Iniciar tour de ayuda', skip: 'Omitir tour' },
  options: { primaryColor: '#0F1F3D', zIndex: 10000, overlayColor: 'rgba(15,47,73,0.45)', showProgress: true, buttons: ['back', 'skip', 'close', 'primary'] } as any,
  styles: { beaconInner: { backgroundColor: '#F97316' }, beaconOuter: { borderColor: '#F97316', backgroundColor: 'rgba(249,115,22,0.2)' }, tooltip: { borderRadius: 8, fontSize: 14 }, tooltipTitle: { fontSize: 15, fontWeight: 700, textAlign: 'left' as const }, tooltipContent: { textAlign: 'left' as const } },
};

const PAGE_SIZE = 5;

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
};

const formatTime = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
};

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Notificaciones');
  const { checkTypeNotification } = useCheckTypeNotification();
  const tokenResponse = useDecode("token");
  const userId = tokenResponse && tokenResponse.extension_id;
  const { isImpersonation } = useGetRole;
  const impersonation = isImpersonation();

  const { notification, setIsUpdated } = useContext(SignalRContext);
  const { updateNotification } = useUpdateNotification();
  const { getAllUserNotifications, allUserNotifications, loadingGetAllUserNotifications } =
    useGetAllNotificationsByUser();

  const [viewedActiveTab, setViewedActiveTab] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [allViewedNotifications, setAllViewedNotifications]       = useState<any[]>([]);
  const [allNotViewedNotifications, setAllNotViewedNotifications] = useState<any[]>([]);
  const [page, setPage] = useState(1);

  const { run, tourKey, steps, handleCallback, startTour } = useNotificationsTour();

  useEffect(() => {
    const handler = () => startTour();
    window.addEventListener('solimat:startNotificationsTour', handler);
    return () => window.removeEventListener('solimat:startNotificationsTour', handler);
  }, [startTour]);

  useEffect(() => { getAllUserNotifications(userId); }, []);

  useEffect(() => {
    if (notification) getAllUserNotifications(userId);
  }, [notification]);

  useEffect(() => {
    setIsUpdated(false);
    if (allUserNotifications) {
      const viewed    = allUserNotifications.filter((n: any) =>  n.leido);
      const notViewed = allUserNotifications.filter((n: any) => !n.leido);
      setAllViewedNotifications(viewed);
      setAllNotViewedNotifications(notViewed);
      if (notViewed.length < 1 && viewed.length >= 1) setViewedActiveTab(true);
    }
  }, [allUserNotifications]);

  const handleSelectTab = (viewed: boolean) => {
    setViewedActiveTab(viewed);
    setPage(1);
  };

  const handleClick = (item: any) => {
    if (impersonation) return;
    const redirectPath = checkTypeNotification(item);
    if (!item.leido) {
      updateNotification(userId, item.id);
      setIsUpdated(true);
      const readItem = { ...item, leido: true };
      setAllNotViewedNotifications((prev) => prev.filter((n: any) => n.id !== item.id));
      setAllViewedNotifications((prev) => [readItem, ...prev]);
    }
    if (redirectPath?.pathname && redirectPath.pathname !== '') {
      navigate(redirectPath.pathname);
    }
  };

  const q = search.trim().toLowerCase();

  const matchesDate = (n: any): boolean => {
    if (!dateFilter) return true;
    if (!n.fechaHora) return false;
    const d = new Date(n.fechaHora);
    const [dd, mm, yyyy] = dateFilter.split('/');
    return (
      d.getFullYear() === parseInt(yyyy) &&
      d.getMonth() === parseInt(mm) - 1 &&
      d.getDate() === parseInt(dd)
    );
  };

  const applyFilters = (items: any[]) =>
    items.filter((n: any) => {
      const textOk = !q || n.titulo?.toLowerCase().includes(q) || n.mensaje?.toLowerCase().includes(q);
      return textOk && matchesDate(n);
    });

  const filteredNotViewed = applyFilters(allNotViewedNotifications);
  const filteredViewed    = applyFilters(allViewedNotifications);
  const list              = viewedActiveTab ? filteredViewed : filteredNotViewed;

  const totalPages  = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const safePage    = Math.min(page, totalPages);
  const paged       = list.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <>
      <Joyride
        key={tourKey}
        steps={steps}
        run={run}
        onEvent={handleCallback}
        {...JOYRIDE_PROPS}
        options={{
          ...JOYRIDE_PROPS.options,
          scrollOffset: getJoyrideScrollOffset(),
        }}
      />

      {loadingGetAllUserNotifications && (
        <Loader activeLoader label="Descargando Notificaciones" />
      )}

      <PageMenu
        title="Notificaciones"
        subtitle="Avisos, actualizaciones y resoluciones de tu expediente"
        breadcrumbs={["Notificaciones"]}
      />

      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="notif-list">

              {/* ── Tabs ── */}
              <div className="tabs">
                <button
                  className={`tabs__tab${!viewedActiveTab ? " is-active" : ""}`}
                  onClick={() => handleSelectTab(false)}
                >
                  No leídas
                  {filteredNotViewed.length > 0 && (
                    <span className="tabs__count tabs__count--warn">{filteredNotViewed.length}</span>
                  )}
                </button>
                <button
                  className={`tabs__tab${viewedActiveTab ? " is-active" : ""}`}
                  onClick={() => handleSelectTab(true)}
                >
                  Leídas
                  {filteredViewed.length > 0 && (
                    <span className="tabs__count">{filteredViewed.length}</span>
                  )}
                </button>
              </div>

              {/* ── Filtros ── */}
              <div className="notif-filters">
                <div className="notif-search">
                  <Icon name="search" size={16} />
                  <input
                    className="notif-search__input"
                    type="text"
                    placeholder="Buscar por título o mensaje…"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {search && (
                    <button
                      className="notif-search__clear"
                      onClick={() => { setSearch(""); setPage(1); }}
                      aria-label="Limpiar búsqueda"
                    >
                      <Icon name="x" size={14} />
                    </button>
                  )}
                </div>
                <div className="notif-date-filter">
                  <InputDatePicker
                    id="notif-date"
                    name="notif-date"
                    placeholder="Filtrar por fecha"
                    value={dateFilter}
                    showClearButton={false}
                    onChange={(val) => { setDateFilter(val); setPage(1); }}
                  />
                  {dateFilter && (
                    <button
                      className="notif-search__clear notif-date-filter__clear"
                      onClick={() => { setDateFilter(null); setPage(1); }}
                      aria-label="Limpiar fecha"
                    >
                      <Icon name="x" size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* ── Lista ── */}
              {paged.length === 0 ? (
                <p className="comms-list__empty">
                  {q ? `Sin resultados para «${search}».` : "No hay notificaciones para mostrar."}
                </p>
              ) : (
                <>
                  <div className="comms notif-cards">
                    {paged.map((item: any, i: number) => (
                      <article
                        key={item.id || i}
                        className={`comm notif-card${!item.leido ? " notif-card--unread" : " notif-card--read"}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleClick(item)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(item); } }}
                      >
                        <div className="notif-card__icon">
                          <Icon name={item.leido ? "check-circle" : "bell"} size={18} />
                        </div>
                        <div className="notif-card__content">
                          <div className="notif-card__title">
                            {item.titulo}
                          </div>
                          {item.mensaje && (
                            <div className="notif-card__body">{item.mensaje}</div>
                          )}
                        </div>
                        <div className="notif-card__meta">
                          {item.fechaHora && (
                            <span className="comm__date-clip">
                              <Icon name="clock" size={11} />
                              <span className="comm__date-clip-num">
                                {formatDate(item.fechaHora)}
                                <span className="notif-card__time"> · {formatTime(item.fechaHora)}</span>
                              </span>
                            </span>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="comms-list__pager">
                      <span className="comms-list__pager-info">
                        {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, list.length)} de {list.length}
                      </span>
                      <div className="comms-list__pager-btns">
                        <button
                          className="btn btn--ghost btn--sm"
                          disabled={safePage <= 1}
                          onClick={() => setPage(safePage - 1)}
                        >
                          <Icon name="arrow-left" size={16} /> Anterior
                        </button>
                        <button
                          className="btn btn--ghost btn--sm"
                          disabled={safePage >= totalPages}
                          onClick={() => setPage(safePage + 1)}
                        >
                          Siguiente <Icon name="arrow-right" size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Notifications;
